import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { api } from "../api/client";
import type { AuthEvent } from "../api/types";
import { attentionSelector, designate } from "../components/attention";
import { Callout } from "../components/Callout";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Dialog, DialogBody } from "../components/Dialog";
import { Motion, Presence } from "../components/presence";
import { Badge, Button, Field } from "../components/ui";
import { t } from "../i18n/i18n";
import { useUi } from "../state/ui";
import { AUTH_SESSION_QUERY, useAuth } from "./auth";
import { FormError, NewPasswordFields, PasswordInput } from "./AuthForm";
import { actionLabel, authDateTime, authErrorText, eventSeat, passwordProblem, sinceLines } from "./authText";
import { Waiting } from "../components/Waiting";

/** Fenêtres du menu du compte (docs/17 §8.3) : changer de mot de passe, mes sessions, mes événements, « Depuis votre
 *  dernière connexion » et l'avertissement de déconnexion. */

/** Changer de mot de passe : le mot de passe actuel est toujours demandé (§4.6) ; les autres sessions sont fermées. */
export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const change = useMutation({
    mutationFn: () => api.auth.password(current, password),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AUTH_SESSION_QUERY });
      notify({ text: t("Mot de passe changé"), detail: t("Vos autres sessions ont été fermées."), tone: "ok" });
      onClose();
    },
    onError: (error) => {
      setCurrent("");
      setProblem(authErrorText(error, "password"));
    },
  });
  const missing = !current
    ? "auth-current-password"
    : passwordProblem(password, confirm)
      ? password.length < 12
        ? "auth-new-password"
        : "auth-confirm-password"
      : null;
  const blocked = () => {
    if (!missing) return;
    if (missing !== "auth-current-password") setProblem(passwordProblem(password, confirm));
    designate(attentionSelector(missing));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (missing) return blocked();
    setProblem(null);
    if (!change.isPending) change.mutate();
  };
  const dirty = Boolean(current || password || confirm);
  return (
    <Dialog
      title={t("Changer de mot de passe")}
      width={420}
      onClose={onClose}
      dirty={dirty}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("Annuler")}
          </Button>
          <Button type="submit" form="auth-password-form" disabled={change.isPending} blocked={Boolean(missing)} onBlocked={blocked}>
            {change.isPending ? <Waiting>{t("Enregistrement…")}</Waiting> : t("Changer le mot de passe")}
          </Button>
        </>
      }
    >
      <DialogBody>
        <form id="auth-password-form" onSubmit={submit} className="space-y-3">
          <Field label={t("Mot de passe actuel")}>
            <PasswordInput value={current} onChange={setCurrent} current attention="auth-current-password" autoFocus />
          </Field>
          <NewPasswordFields password={password} confirm={confirm} onPassword={setPassword} onConfirm={setConfirm} />
          <FormError message={problem} />
        </form>
      </DialogBody>
    </Dialog>
  );
}

const SESSIONS_QUERY = ["auth-sessions"] as const;

/** Mes sessions : sessions ouvertes du compte (5 au plus), celle-ci désignée ; les autres se ferment d'ici. */
export function MySessionsDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const sessions = useQuery({ queryKey: SESSIONS_QUERY, queryFn: api.auth.sessions, staleTime: 0 });
  const end = useMutation({
    mutationFn: api.auth.endSession,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY }),
  });
  return (
    <Dialog title={t("Mes sessions")} subtitle={t("Sessions ouvertes")} width={560} onClose={onClose}>
      <DialogBody>
        {sessions.error ? (
          <Callout tone="danger">{sessions.error.message}</Callout>
        ) : !sessions.data ? (
          <p className="text-ui text-ink-muted">{<Waiting>{t("Chargement…")}</Waiting>}</p>
        ) : (
          <Motion origin="fade">
            <div className="hidden grid-cols-[1fr_auto_auto_auto] gap-x-3 border-b border-line pb-1.5 text-meta font-medium text-ink-muted sm:grid">
              <span>{t("Poste")}</span>
              <span>{t("Ouverte le")}</span>
              <span>{t("Dernière activité")}</span>
              <span className="w-20" />
            </div>
            <ul>
              {sessions.data.map((row) => (
                <li
                  key={row.ref}
                  className="grid min-h-8 grid-cols-[1fr_auto] items-center gap-x-3 border-b border-line py-1.5 last:border-b-0 sm:grid-cols-[1fr_auto_auto_auto]"
                >
                  <span className="min-w-0 text-ui leading-snug break-words text-ink">{row.seat_name || t("Poste sans nom")}</span>
                  <span className="hidden text-meta text-ink-muted tabular-nums sm:block">{authDateTime(row.created_at)}</span>
                  <span className="hidden text-meta text-ink-muted tabular-nums sm:block">{authDateTime(row.last_active_at)}</span>
                  <span className="flex w-20 justify-end">
                    {row.current ? (
                      <Badge tone="accent">{t("Cette session")}</Badge>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => end.mutate(row.ref)} disabled={end.isPending}>
                        {t("Fermer")}
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Motion>
        )}
        <Presence show={Boolean(end.error)}>{end.error && <Callout tone="danger">{end.error.message}</Callout>}</Presence>
      </DialogBody>
    </Dialog>
  );
}

/** Mes événements : connexions, échecs sur mon identifiant, codes, sessions fermées, rôle, sur 90 jours (§5.8). */
export function MyEventsDialog({ onClose }: { onClose: () => void }) {
  const events = useQuery({ queryKey: ["auth-events"], queryFn: api.auth.events, staleTime: 0 });
  return (
    <Dialog title={t("Mes événements")} subtitle={t("90 derniers jours")} width={680} onClose={onClose}>
      <DialogBody>
        {events.error ? (
          <Callout tone="danger">{events.error.message}</Callout>
        ) : !events.data ? (
          <p className="text-ui text-ink-muted">{<Waiting>{t("Chargement…")}</Waiting>}</p>
        ) : events.data.length === 0 ? (
          <p className="text-ui text-ink-muted">{t("Aucun événement sur cette période.")}</p>
        ) : (
          <Motion origin="fade">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-line text-meta text-ink-muted">
                  <th className="py-1.5 pr-3 font-medium">{t("Date")}</th>
                  <th className="py-1.5 pr-3 font-medium">{t("Événement")}</th>
                  <th className="py-1.5 pr-3 font-medium">{t("Poste")}</th>
                  <th className="py-1.5 font-medium">{t("Par")}</th>
                </tr>
              </thead>
              <tbody>
                {events.data.map((event: AuthEvent, index) => (
                  <tr key={`${event.at}-${index}`} className="border-b border-line align-top text-ui last:border-b-0">
                    <td className="py-1.5 pr-3 whitespace-nowrap text-ink-muted tabular-nums">{authDateTime(event.at)}</td>
                    <td className="py-1.5 pr-3 text-ink">{actionLabel(event.action)}</td>
                    <td className="py-1.5 pr-3 break-words text-ink-soft">{eventSeat(event)}</td>
                    <td className="py-1.5 break-words text-ink-soft">{event.by || event.actor || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Motion>
        )}
      </DialogBody>
    </Dialog>
  );
}

/** Depuis votre dernière connexion : montrée une fois après la connexion, s'il y a lieu. */
export function SinceLastLoginDialog({ events, onClose }: { events: AuthEvent[]; onClose: () => void }) {
  const mounted = useAuth((s) => s.mounted);
  const lines = sinceLines(events, mounted ? { id: mounted.id, display_name: mounted.display_name } : null);
  return (
    <Dialog
      title={t("Depuis votre dernière connexion")}
      width={480}
      onClose={onClose}
      footer={<Button onClick={onClose}>{t("J'ai compris")}</Button>}
    >
      <DialogBody>
        <ul className="space-y-2">
          {lines.map((line) => (
            <li key={line} className="text-ui leading-relaxed text-ink">
              {line}
            </li>
          ))}
        </ul>
        <p className="border-t border-line pt-3 text-meta leading-snug text-ink-muted">
          {t("Ce n'était pas vous, ou vous ne l'aviez pas demandé ? Prévenez un autre administrateur de l'unité.")}
        </p>
      </DialogBody>
    </Dialog>
  );
}

/** Lignes à montrer après une connexion (vide : pas de fenêtre). */
export function hasSinceLines(events: AuthEvent[]): boolean {
  const mounted = useAuth.getState().mounted;
  return sinceLines(events, mounted ? { id: mounted.id, display_name: mounted.display_name } : null).length > 0;
}

/** Déconnexion avec des saisies non enregistrées (§8.3) : revenir les enregistrer (choix par défaut), ou se déconnecter
 *  quand même, ce qui les efface de ce poste, dans tous ses onglets. */
export function SignOutDialog({ labels, busy, onConfirm, onCancel }: { labels: string[]; busy: boolean; onConfirm: () => void; onCancel: () => void }) {
  return (
    <ConfirmDialog
      title={t("Saisies non enregistrées")}
      confirmLabel={t("Se déconnecter quand même")}
      cancelLabel={t("Revenir pour enregistrer")}
      busy={busy}
      busyLabel={t("Déconnexion…")}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {t("{liste} : se déconnecter les efface de ce poste, dans tous ses onglets. Revenez les enregistrer si vous voulez les garder.", {
        liste: labels.length ? labels.join(" · ") : t("Saisies gardées sur ce poste"),
      })}
    </ConfirmDialog>
  );
}
